import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireEmailVerification?: boolean;
}

export default function ProtectedRoute({ 
  children, 
  requireEmailVerification = true 
}: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const location = useLocation();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        setIsAuthenticated(!!session);

        if (currentUser) {
          Sentry.setUser({ id: currentUser.id, email: currentUser.email });
          setIsEmailVerified(!!currentUser.email_confirmed_at);
        } else {
          Sentry.setUser(null);
          setIsEmailVerified(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Still loading
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated but email not verified - redirect to check-email
  if (requireEmailVerification && !isEmailVerified) {
    return <Navigate to="/check-email" state={{ email: user?.email }} replace />;
  }

  return <>{children}</>;
}
